import * as React from "react"

import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"
import { Loader } from "../Loader/Loader"
import { Tooltip, type TooltipProps } from "../Tooltip/Tooltip"

const buttonVariants = cva(
  [
    "inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap",
    "h-control px-3 rounded-md text-sm font-medium [&>svg]:size-3.5",
    "transition-colors duration-150",
    "disabled:cursor-default disabled:opacity-50",
    "aria-busy:cursor-progress",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-text",
  ],
  {
    variants: {
      variant: {
        default:
          "bg-coval-contrast text-coval-text-on-contrast hover:bg-coval-contrast-hover disabled:hover:bg-coval-contrast",
        secondary:
          "bg-surface-default border border-surface-1 text-coval-text hover:bg-surface-contrast disabled:hover:bg-surface-default",
        ghost:
          "bg-transparent text-coval-text-muted hover:bg-surface-1 hover:text-coval-text disabled:hover:bg-transparent disabled:hover:text-coval-text-muted",
        danger:
          "bg-coval-danger-surface text-coval-danger-on-surface hover:bg-coval-danger-surface-hover disabled:hover:bg-coval-danger-surface",
        link: "h-auto px-0 bg-transparent text-coval-text underline underline-offset-2 hover:text-coval-text-muted disabled:hover:text-coval-text",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

/** The `Tooltip` knobs a button can forward, minus the two it owns itself. */
export type ButtonTooltipProps = Omit<TooltipProps, "content" | "children">

interface ButtonBaseProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  active?: boolean
  /**
   * Marks the button's action as in flight: disables it, sets `aria-busy`,
   * and shows a spinner (replacing the icon on `icon` buttons, prepended to
   * the label otherwise). No spinner is injected with `asChild`, since
   * `Slot` requires exactly one child.
   */
  loading?: boolean
}

interface ButtonIconBaseProps {
  /** Renders as a square, icon-only control. */
  icon: boolean
  /**
   * Shown instead of `tooltip` while the button is disabled or loading, for
   * saying *why* the control is unavailable. Falls back to `tooltip` when a
   * reason would add nothing.
   */
  disabledReason?: React.ReactNode
  /**
   * Forwarded to the underlying `Tooltip`, for the exceptions its own props
   * exist for: a deliberate `side`, feedback that has to outlive a click
   * (`forceOpen`), or a test hook.
   */
  tooltipProps?: ButtonTooltipProps
}

/**
 * What the icon means. Required alongside `icon`, because an icon with no words
 * is only guessable.
 *
 * A plain string is the whole contract on its own: the button renders the
 * `Tooltip` and derives `aria-label` from the same string, so the visible label
 * and the accessible one cannot drift apart. Anything the derivation cannot
 * read — markup, several lines, or an expression that may be `undefined` —
 * has to carry an `aria-label` of its own, since a tooltip that never resolves
 * to a string would otherwise leave the control both unlabelled and
 * unexplained. Requiring it here is what keeps the type from advertising more
 * than the derivation honors.
 */
type ButtonIconLabelProps =
  | { tooltip: string; "aria-label"?: string }
  | { tooltip: React.ReactNode; "aria-label": string }

type ButtonIconProps = ButtonIconBaseProps & ButtonIconLabelProps

interface ButtonTextProps {
  icon?: false
  /**
   * Only icon buttons carry their own tooltip. A labelled button that needs
   * one wraps in `Tooltip` directly, so the two cases stay distinguishable.
   */
  tooltip?: never
  disabledReason?: never
  tooltipProps?: never
}

export type ButtonProps = ButtonBaseProps & (ButtonIconProps | ButtonTextProps)

/**
 * A disabled control emits no pointer events at all, so a click on it never
 * reaches an ancestor's handler — which is why a disabled button inside a
 * clickable card does nothing rather than selecting the card. The wrapper below
 * is an ordinary element and would happily pass those clicks up, so it
 * re-swallows exactly what the button no longer emits. Only propagation stops:
 * `preventDefault` would tell Radix's composed handlers the event was already
 * handled and take the tooltip down with it.
 */
const swallowPointerEvent = (event: React.SyntheticEvent) => {
  event.stopPropagation()
}

const DISABLED_WRAPPER_HANDLERS = {
  onClick: swallowPointerEvent,
  onMouseDown: swallowPointerEvent,
  onMouseUp: swallowPointerEvent,
  onPointerDown: swallowPointerEvent,
  onPointerUp: swallowPointerEvent,
} as const

/**
 * `Omit` collapses a union to the keys its members share, which would quietly
 * drop `tooltip` and its companions from anything derived from `ButtonProps`.
 * This distributes over the union instead, so each branch keeps its own keys.
 */
export type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never

/**
 * The implementation sees one flat prop bag; callers see the union above. The
 * cast below is what puts the union in front of them, so a `<Button icon>` with
 * no `tooltip` is a type error rather than a silent omission.
 */
const ButtonImpl = React.forwardRef<
  HTMLButtonElement,
  ButtonBaseProps & Partial<ButtonIconBaseProps> & { tooltip?: React.ReactNode }
>(
  (
    {
      className,
      variant,
      asChild = false,
      active,
      icon,
      loading,
      disabled,
      tooltip,
      disabledReason,
      tooltipProps,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button"
    const isDisabled = disabled || loading
    const hasTooltip = Boolean(icon) && tooltip != null
    const accessibleName =
      props["aria-label"] ??
      (icon && typeof tooltip === "string" ? tooltip : undefined)
    /**
     * A disabled `<button>` takes no focus, so the tooltip hanging off it is
     * reachable by pointer alone — and a disabled control is exactly where a
     * keyboard or screen-reader user most needs to hear why. The wrapper stands
     * in for the button instead: focusable, announced as a disabled button
     * under the same name, and described by the tooltip Radix attaches to it.
     */
    const standsInForControl = hasTooltip && isDisabled

    const control = (
      <Comp
        className={cn(
          buttonVariants({ variant }),
          icon && "h-control aspect-square p-0",
          active && "border border-coval-contrast text-coval-contrast",
          // A disabled control dispatches no pointer events, so a tooltip
          // wrapped around it would never open — exactly when the user most
          // needs to know why it is unavailable. Letting the pointer through
          // hands the event to the wrapper below, which is not disabled.
          standsInForControl && "pointer-events-none",
          className,
        )}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...props}
        aria-label={accessibleName}
        aria-hidden={standsInForControl || undefined}
        // `disabled` means nothing to a slotted anchor, so the wrapper's tab
        // stop would be a second one rather than the only one — and a focusable
        // element under `aria-hidden` is a trap on its own. Taking the child out
        // of the tab order leaves exactly the wrapper to focus. A disabled
        // `<button>` is unfocusable already and ignores this.
        tabIndex={standsInForControl ? -1 : props.tabIndex}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading && <Loader size="sm" className="size-3.5 text-current" />}
            {loading && icon ? null : children}
          </>
        )}
      </Comp>
    )

    if (!hasTooltip) {
      return control
    }

    return (
      <Tooltip
        content={(isDisabled && disabledReason) || tooltip}
        {...tooltipProps}
      >
        {standsInForControl ? (
          // Stays hoverable and focusable while the button inside it is
          // neither, so a disabled control can still explain itself. The button
          // is hidden from assistive technology so the pair is announced once.
          // `inline-flex` keeps the wrapper out of the way of whatever lays the
          // button out.
          <span
            className={cn(
              "inline-flex rounded-md",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-text",
            )}
            tabIndex={0}
            role="button"
            aria-disabled
            aria-busy={loading || undefined}
            aria-label={accessibleName}
            {...DISABLED_WRAPPER_HANDLERS}
          >
            {control}
          </span>
        ) : (
          control
        )}
      </Tooltip>
    )
  },
)

ButtonImpl.displayName = "Button"

const Button = ButtonImpl as React.ForwardRefExoticComponent<
  ButtonProps & React.RefAttributes<HTMLButtonElement>
>

export { Button, buttonVariants }
